import * as eventCreators from './events';

/**
 * Push event to dataLayer
 * @param {Object} eventData - Event data to push
 */
function pushToDataLayer(eventData) {
  if (typeof window === 'undefined') return;

  // Initialize dataLayer if it doesn't exist
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  // Clear ecommerce object before pushing new data (GA4 best practice)
  if (eventData.ecommerce) {
    window.dataLayer.push({ ecommerce: null });
  }

  // Add newTracking flag inside ecommerce object
  const eventWithFlag = eventData.ecommerce
    ? {
        ...eventData,
        ecommerce: {
          ...eventData.ecommerce,
          newTracking: true
        }
      }
    : { ...eventData, newTracking: true };

  // Push the event
  window.dataLayer.push(eventWithFlag);
}

/**
 * Main tracking class
 */
class Tracking {
  constructor() {
    this._isInitialLoad = true;
  }

  /**
   * Track view_item_list event
   * @param {Object} params - Event parameters
   */
  viewItemList(params) {
    const event = eventCreators.createViewItemListEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track select_item event
   * @param {Object} params - Event parameters
   */
  selectItem(params) {
    const event = eventCreators.createSelectItemEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track view_item event
   * @param {Object} params - Event parameters
   */
  viewItem(params) {
    const event = eventCreators.createViewItemEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track add_to_cart event
   * @param {Object} params - Event parameters
   */
  addToCart(params) {
    const event = eventCreators.createAddToCartEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track remove_from_cart event
   * @param {Object} params - Event parameters
   */
  removeFromCart(params) {
    const event = eventCreators.createRemoveFromCartEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track view_cart event
   * @param {Object} params - Event parameters
   */
  viewCart(params) {
    const event = eventCreators.createViewCartEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track view_mini_cart event
   * @param {Object} params - Event parameters
   */
  viewMiniCart(params) {
    const event = eventCreators.createViewMiniCartEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track begin_checkout event
   * @param {Object} params - Event parameters
   */
  beginCheckout(params) {
    const event = eventCreators.createBeginCheckoutEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track purchase event
   * @param {Object} params - Event parameters
   */
  purchase(params) {
    const event = eventCreators.createPurchaseEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track view_promotion event
   * @param {Object} params - Event parameters
   */
  viewPromotion(params) {
    const event = eventCreators.createViewPromotionEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track select_promotion event
   * @param {Object} params - Event parameters
   */
  selectPromotion(params) {
    const event = eventCreators.createSelectPromotionEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track search event
   * @param {Object} params - Event parameters
   */
  search(params) {
    const event = eventCreators.createSearchEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track page_view event
   * @param {Object} params - Event parameters
   */
  pageView(params) {
    const event = eventCreators.createPageViewEvent({
      ...params,
      type: this._isInitialLoad ? 'hard' : 'dynamic'
    });
    this._isInitialLoad = false;
    pushToDataLayer(event);
  }

  /**
   * Track search_result_click event
   * @param {Object} params - Event parameters
   */
  searchResultClick(params) {
    const event = eventCreators.createSearchResultClickEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Track popular_category_click event
   * @param {Object} params - Event parameters
   */
  popularCategoryClick(params) {
    const event = eventCreators.createPopularCategoryClickEvent(params);
    pushToDataLayer(event);
  }

  /**
   * Push custom event to dataLayer
   * @param {string} eventName - Event name
   * @param {Object} eventData - Event data
   */
  customEvent(eventName, eventData = {}) {
    pushToDataLayer({
      event: eventName,
      ...eventData
    });
  }
}

// Create singleton instance
const tracking = new Tracking();

// Export the singleton instance as default
export default tracking;

// Also export the class for testing
export { Tracking };

// Export event creators for advanced use cases
export * from './events';
export * from './formatters';
export * from './constants';
